import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { RotateCcw, MapPin, Calendar, Pencil, Trash2, Check, X } from "lucide-react";

const SAMPLE_HISTORY = [
  {
    id: 1,
    port: "Port of Barcelona",
    country: "Spain 🇪🇸",
    date: "2024-04-20",
    status: "approved",
    vessel: "SS Marina",
    documentsUsed: 8
  },
  {
    id: 2,
    port: "Port of Monaco",
    country: "Monaco 🇲🇨",
    date: "2024-04-15",
    status: "pending",
    vessel: "SS Marina",
    documentsUsed: 8
  },
  {
    id: 3,
    port: "Port of Mallorca",
    country: "Spain 🇪🇸",
    date: "2024-04-10",
    status: "approved",
    vessel: "SS Marina",
    documentsUsed: 8
  }
];

export default function PortEntryHistory({ user, onCreateNew }) {
  const [history, setHistory] = useState(SAMPLE_HISTORY);
  const [editingId, setEditingId] = useState(null);
  const [editData, setEditData] = useState({});
  const [deletingId, setDeletingId] = useState(null);

  const startEdit = (entry) => {
    setEditingId(entry.id);
    setEditData({ port: entry.port, country: entry.country, date: entry.date, status: entry.status, vessel: entry.vessel });
  };

  const saveEdit = (id) => {
    setHistory(prev => prev.map(e => e.id === id ? { ...e, ...editData } : e));
    setEditingId(null);
  };

  const cancelEdit = () => setEditingId(null);

  const confirmDelete = (id) => {
    setHistory(prev => prev.filter(e => e.id !== id));
    setDeletingId(null);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "approved":
        return "bg-green-100 text-green-800";
      case "pending":
        return "bg-yellow-100 text-yellow-800";
      case "issue":
        return "bg-red-100 text-red-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold">📊 Entry Pack History</h2>
            <p className="text-sm text-muted-foreground mt-1">Previous port submissions & entry packs</p>
          </div>
          <Button onClick={onCreateNew} className="gap-2">
            ➕ New Entry Pack
          </Button>
        </div>
      </motion.div>

      {/* History List */}
      <motion.div
        className="space-y-3"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.1 }}
      >
        <AnimatePresence>
        {history.map((entry, idx) => (
          <motion.div
            key={entry.id}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={{ delay: editingId ? 0 : 0.05 * idx }}
          >
            <Card className="hover:shadow-lg transition-shadow">
              <CardContent className="p-4">
                {editingId === entry.id ? (
                  /* ── Edit mode ── */
                  <div className="space-y-3">
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label className="text-xs text-muted-foreground mb-1 block">Port</label>
                        <Input value={editData.port} onChange={e => setEditData(d => ({ ...d, port: e.target.value }))} />
                      </div>
                      <div>
                        <label className="text-xs text-muted-foreground mb-1 block">Vessel</label>
                        <Input value={editData.vessel} onChange={e => setEditData(d => ({ ...d, vessel: e.target.value }))} />
                      </div>
                      <div>
                        <label className="text-xs text-muted-foreground mb-1 block">Date</label>
                        <Input type="date" value={editData.date} onChange={e => setEditData(d => ({ ...d, date: e.target.value }))} />
                      </div>
                      <div>
                        <label className="text-xs text-muted-foreground mb-1 block">Status</label>
                        <Select value={editData.status} onValueChange={v => setEditData(d => ({ ...d, status: v }))}>
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="approved">Approved</SelectItem>
                            <SelectItem value="pending">Pending</SelectItem>
                            <SelectItem value="issue">Issue</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div className="flex gap-2 justify-end">
                      <Button size="sm" variant="outline" onClick={cancelEdit} className="gap-1"><X className="w-3.5 h-3.5" /> Cancel</Button>
                      <Button size="sm" onClick={() => saveEdit(entry.id)} className="gap-1"><Check className="w-3.5 h-3.5" /> Save</Button>
                    </div>
                  </div>
                ) : deletingId === entry.id ? (
                  /* ── Delete confirm ── */
                  <div className="flex items-center justify-between gap-4">
                    <p className="text-sm text-red-600 font-medium">Delete <span className="font-bold">{entry.port}</span>? This cannot be undone.</p>
                    <div className="flex gap-2 flex-shrink-0">
                      <Button size="sm" variant="outline" onClick={() => setDeletingId(null)}>Cancel</Button>
                      <Button size="sm" variant="destructive" onClick={() => confirmDelete(entry.id)} className="gap-1"><Trash2 className="w-3.5 h-3.5" /> Delete</Button>
                    </div>
                  </div>
                ) : (
                  /* ── Normal view ── */
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-2">
                        <h3 className="font-semibold text-sm">{entry.port}</h3>
                        <span className="text-sm">{entry.country}</span>
                      </div>
                      <div className="flex flex-wrap gap-2 text-xs text-muted-foreground mb-3">
                        <div className="flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          {new Date(entry.date).toLocaleDateString()}
                        </div>
                        <div className="flex items-center gap-1">
                          <MapPin className="w-3 h-3" />
                          {entry.vessel}
                        </div>
                        <div>📄 {entry.documentsUsed} documents</div>
                      </div>
                      <Badge className={getStatusColor(entry.status)}>
                        {entry.status === "approved" ? "✓ Approved" : entry.status === "pending" ? "⏳ Pending" : "⚠️ Issue"}
                      </Badge>
                    </div>
                    <div className="flex gap-1 flex-shrink-0">
                      <Button variant="ghost" size="sm" onClick={() => startEdit(entry)} className="gap-1 text-muted-foreground hover:text-foreground">
                        <Pencil className="w-3.5 h-3.5" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => setDeletingId(entry.id)} className="gap-1 text-muted-foreground hover:text-destructive">
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                      <Button variant="outline" size="sm" className="gap-1 ml-1">
                        <RotateCcw className="w-3.5 h-3.5" />
                        <span className="hidden sm:inline">Reuse</span>
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </motion.div>
        ))}
        </AnimatePresence>
      </motion.div>

      {/* Stats */}
      <motion.div
        className="grid grid-cols-3 gap-4"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.5 }}
      >
        <Card>
          <CardContent className="pt-6">
            <p className="text-xs text-muted-foreground">Total Entries</p>
            <p className="text-2xl font-bold">{history.length}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <p className="text-xs text-muted-foreground">Approved</p>
            <p className="text-2xl font-bold text-green-600">{history.filter(e => e.status === "approved").length}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <p className="text-xs text-muted-foreground">Pending</p>
            <p className="text-2xl font-bold text-yellow-600">{history.filter(e => e.status === "pending").length}</p>
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
}